import React from 'react';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  if (!content) return null;

  // Split content into lines for processing
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let currentParagraph: string[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeaders: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ').trim();
      if (text) {
        elements.push(
          <p key={`p-${elements.length}`} className="mb-4 text-gray-700 leading-relaxed">
            {renderInlineMarkdown(text)}
          </p>
        );
      }
      currentParagraph = [];
    }
  };

  const renderInlineMarkdown = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    
    // Bold **text**
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(<strong key={`bold-${parts.length}`} className="font-semibold text-gray-900">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  const renderTable = (headers: string[], rows: string[][]) => {
    if (headers.length === 0 || rows.length === 0) return null;

    return (
      <div key={`table-${elements.length}`} className="my-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gradient-to-r from-indigo-500 to-purple-600">
            <tr>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider"
                >
                  {header.trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                {row.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                  >
                    {cell.trim()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Process lines
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check for markdown table
    if (line.includes('|') && line.split('|').length > 2) {
      if (!inTable) {
        inTable = true;
        flushParagraph();
        // First line is headers
        tableHeaders = line.split('|').map(c => c.trim()).filter(c => c);
        // Skip separator line (|---|---|)
        if (i + 1 < lines.length && lines[i + 1].includes('---')) {
          i++;
          continue;
        }
      }
      
      const cells = line.split('|').map(c => c.trim()).filter(c => c);
      if (cells.length > 0) {
        tableRows.push(cells);
      }
      continue;
    } else {
      // End of table
      if (inTable) {
        if (tableHeaders.length > 0 && tableRows.length > 0) {
          elements.push(renderTable(tableHeaders, tableRows));
        }
        inTable = false;
        tableHeaders = [];
        tableRows = [];
      }

      // Headers (# ## ###)
      if (line.startsWith('###')) {
        flushParagraph();
        elements.push(
          <h3 key={`h3-${elements.length}`} className="text-lg font-bold text-gray-900 mt-6 mb-3">
            {line.substring(3).trim()}
          </h3>
        );
      } else if (line.startsWith('##')) {
        flushParagraph();
        elements.push(
          <h2 key={`h2-${elements.length}`} className="text-xl font-bold text-gray-900 mt-6 mb-4">
            {line.substring(2).trim()}
          </h2>
        );
      } else if (line.startsWith('#')) {
        flushParagraph();
        elements.push(
          <h1 key={`h1-${elements.length}`} className="text-2xl font-bold text-gray-900 mt-6 mb-4">
            {line.substring(1).trim()}
          </h1>
        );
      }
      // Bullet points (- or *)
      else if (line.startsWith('-') || line.startsWith('*')) {
        flushParagraph();
        const listItem = line.substring(1).trim();
        elements.push(
          <li key={`li-${elements.length}`} className="ml-6 mb-2 text-gray-700 list-disc">
            {renderInlineMarkdown(listItem)}
          </li>
        );
      }
      // Numbered lists
      else if (/^\d+\./.test(line)) {
        flushParagraph();
        const listItem = line.replace(/^\d+\./, '').trim();
        elements.push(
          <li key={`li-${elements.length}`} className="ml-6 mb-2 text-gray-700 list-decimal">
            {renderInlineMarkdown(listItem)}
          </li>
        );
      }
      // Empty line
      else if (line === '') {
        flushParagraph();
      }
      // Regular text
      else {
        currentParagraph.push(line);
      }
    }
  }

  // Flush remaining paragraph and table
  flushParagraph();
  if (inTable && tableHeaders.length > 0 && tableRows.length > 0) {
    elements.push(renderTable(tableHeaders, tableRows));
  }

  // Wrap list items in ul/ol
  const wrappedElements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;

  elements.forEach((el) => {
    if (React.isValidElement(el) && el.type === 'li') {
      const isOrdered = el.key?.toString().includes('decimal');
      const newListType = isOrdered ? 'ol' : 'ul';
      
      if (listType !== newListType && currentList.length > 0) {
        wrappedElements.push(
          React.createElement(listType === 'ul' ? 'ul' : 'ol', {
            key: `list-${wrappedElements.length}`,
            className: 'mb-4 space-y-1 ml-6',
          }, currentList)
        );
        currentList = [];
      }
      
      listType = newListType;
      currentList.push(el);
    } else {
      if (currentList.length > 0) {
        wrappedElements.push(
          React.createElement(listType === 'ul' ? 'ul' : 'ol', {
            key: `list-${wrappedElements.length}`,
            className: 'mb-4 space-y-1 ml-6',
          }, currentList)
        );
        currentList = [];
        listType = null;
      }
      wrappedElements.push(el);
    }
  });

  if (currentList.length > 0) {
    wrappedElements.push(
      React.createElement(listType === 'ul' ? 'ul' : 'ol', {
        key: `list-${wrappedElements.length}`,
        className: 'mb-4 space-y-1 ml-6',
      }, currentList)
    );
  }

  return (
    <div className="prose prose-indigo max-w-none">
      {wrappedElements.length > 0 ? wrappedElements : <p className="text-gray-700 whitespace-pre-wrap">{content}</p>}
    </div>
  );
};

export default MarkdownRenderer;
